/**
 * "When I am being attacked, it is not obvious" feedback — a `combat.result`
 * hit on the local player only ever appended a line to DebugHud's scrolling
 * log, easy to miss mid-fight. This is a brief full-screen red vignette,
 * triggered whenever the local player's own hp drops.
 */
const FLASH_HOLD_MS = 80;
const FLASH_FADE_MS = 450;

export class DamageFlash {
  private readonly root: HTMLDivElement;
  private fadeTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(container: HTMLElement) {
    this.root = document.createElement('div');
    this.root.className = 'fixed inset-0 z-[1100] pointer-events-none opacity-0';
    this.root.style.transitionProperty = 'opacity';
    this.root.style.transitionTimingFunction = 'ease-out';
    this.root.style.background = 'radial-gradient(ellipse at center, rgba(139,0,0,0) 40%, rgba(139,0,0,0.6) 100%)';
    container.appendChild(this.root);
  }

  /** Off when the player has turned the flash off in settings. */
  private enabled = true;

  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  trigger(): void {
    if (!this.enabled) return;
    if (this.fadeTimeout) clearTimeout(this.fadeTimeout);
    // Jump to fully visible with no transition, then re-enable the fade on
    // the next frame — otherwise the browser animates the jump-to-visible
    // step too, and repeated hits never look like a sharp flash.
    this.root.style.transitionDuration = '0ms';
    this.root.style.opacity = '1';
    requestAnimationFrame(() => {
      this.root.style.transitionDuration = `${FLASH_FADE_MS}ms`;
      this.fadeTimeout = setTimeout(() => {
        this.root.style.opacity = '0';
      }, FLASH_HOLD_MS);
    });
  }

  dispose(): void {
    if (this.fadeTimeout) clearTimeout(this.fadeTimeout);
    this.root.remove();
  }
}
